<template>
  <div>
    <v-col class="px-0">
      <fiche-header
          :hide-chevron="true"
          :show-search="false"
          :title="tableName"
          @back="back">
      </fiche-header>
    </v-col>
    <v-row class="fiche">
      <v-col class="col-12">
        <div class="section p-4">
          <v-data-table
              :headers="headers"
              :height="listHeight"
              no-data-text="Aucune donnée"
              :items="items"
              :items-per-page="-1"
              class="white module-list"
              :row-props="notifRowProps"
              disable-pagination
              fixed-header
              hide-default-footer
              :mobile-breakpoint="100"
              @update:sortBy="onSortUpdate"
              @current-items="setCurrentItems">
            <template #top>
              <v-row class="m-0">
                <v-col cols="8">
                  <search-filter-input :value="searchText" @input-updated="onSearchTextUpdated"></search-filter-input>
                </v-col>

                <div class="d-flex align-center col justify-end">
                  <loader-button
                      v-if="adminConfig.canImport"
                      :label="importButton"
                      class="ms-2"
                      @callback="openImport">
                  </loader-button>
                  <loader-button
                      v-if="canExport"
                      :label="exportButton"
                      :disabled="disableExport"
                      class="ms-2"
                      @callback="openExport">
                  </loader-button>
                  <loader-button
                      v-if="adminConfig.canCreate"
                      :label="addLabel"
                      class="ms-2"
                      @callback="addItem">
                  </loader-button>
                </div>
              </v-row>
            </template>

            <template v-for="(col, index) in headers" #[`item.${col.value}`]="{ item  }">
              <span :key="index" :class="getClass(item)">{{ getLabel(col.value, item[col.value]) }}</span>
            </template>

            <template #[`item.canUpdate`]="{ item }">
              <v-icon
                  v-if="table == 'ContratTemplates' && item.canUpdate"
                  class="me-2"
                  @click.stop="cloneTableItem(item)">
                fas fa-copy
              </v-icon>
              <v-icon
                  v-if="item.canUpdate"
                  @click="editItem(item)">
                fas fa-pencil
              </v-icon>
            </template>

            <template #[`item.canDelete`]="{ item }">
              <v-icon
                  v-if="item.canDelete"
                  @click="deleteItem(item)">
                fas fa-trash-can
              </v-icon>
            </template>

            <template v-for="(item, index) in headers" #[`header.${item.value}`]>
              <div :key="index" style="display: flex; align-items: center; justify-content: space-between;">
                <div><span>{{ item.text }}</span></div>
                <v-icon class="sort-icon p-1" small>fas fa-arrow-up</v-icon>
                <data-table-filter
                    :header-value="item.value"
                    :column-config="getConfig(item.value)"
                    :current-filters="getFilters(item.value)"
                    @apply="applyFilters">
                </data-table-filter>
              </div>
            </template>
          </v-data-table>
        </div>
        <v-row class="m-0">
          <v-col cols="11">
            <v-pagination v-if="pageCount > 1" v-model="page" :length="pageCount" :total-visible="10"
                          class="pagination-color"></v-pagination>
          </v-col>
          <v-col class="text-right align-content-center">
            {{ filteredCount }}/{{ totalCount }}
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <section-container
        :dialog-open="showSection"
        :extended="true"
        :fullscreen="section.fullscreen"
        :section="section"
        @close-dialog="closeDialog"
        @updated="updateTable">
    </section-container>
  </div>
</template>
<script lang="ts">
import LoaderButton from '@/framework/base/components/LoaderButton.vue';
import SectionContainer from '@/framework/base/components/SectionContainer.vue';
import SectionAdmin from '@/framework/admin/components/SectionAdmin.vue';
import SearchFilterInput from '@/framework/base/components/SearchFilterInput.vue';
import DataTableFilter from '@/framework/base/components/DataTableFilter.vue';
import Section from '@/framework/base/classes/Section';
import ImportDialog from '@/framework/admin/components/ImportDialog.vue';
import ExportDialog from '@/framework/admin/components/ExportDialog.vue';
import Ressource from '@/framework/ressource/classes/Ressource';
import Admin, {AdminConfig, AdminExport, AdminImport, AdminObj, AdminObjActions} from '@/generated/classes/Admin';
import AdminAttrib from '@/extend/classes/AdminAttrib';
import {getAdminAttribs, editItem, addItem, cloneItem} from '@/extend/fiches/FicheAdminExtend.vue';
import Dialog from '@/generated/classes/Dialog';
import {FwkButtons, RessourceFwkButton} from '@/ressources/globalRessources';
import {openAlert} from '@/framework/base/components/SectionAlert.vue';
import FicheHeader from '@/framework/base/components/FicheHeader.vue';
import ListBase from '@/framework/base/components/ListBase';
import ListData, {ColumnConfig, ListFilter} from '@/extend/classes/ListData';
import AdminInfo from '@/generated/classes/AdminInfo';
import Route from '@/extend/classes/Route';
import {NotifDefTypes} from '@/generated/classes/NotifDef';
import {defineComponent, type PropType} from "vue";

export default defineComponent({
  components: {
    SectionContainer,
    LoaderButton,
    SearchFilterInput,
    DataTableFilter,
    FicheHeader
  },
  extends: ListBase,
  props: {
    adminInfo: {
      type: Object as PropType<AdminInfo>,
      required: true
    },
    toggleNotif: {
      type: Boolean
    }
  },
  data() {
    const adminInfos: AdminInfo[] = [];
    const tableFilters: Record<string, string[]> = {};
    const displayedIds: number[] = [];

    return {
      displayedIds,
      listHeight: 'calc(100vh - 275px)',
      showSection: false,
      tableFilters,
      section: new Section(),
      lastTable: "",
      adminInfos,
      notifLoaded: false
    };
  },
  computed: {
    importButton(): string {
      if (this.adminImport.table == 'AdminAttribs') {
        return RessourceFwkButton(FwkButtons.INITIALISER).label;
      } else {
        return RessourceFwkButton(FwkButtons.IMPORTER).label;
      }
    },
    exportButton(): string {
      return RessourceFwkButton(FwkButtons.EXPORTER).label;
    },
    headers(): AdminAttrib[] {
      const listHeaders: AdminAttrib[] = [];
      const editHeader = new AdminAttrib();
      editHeader.key = "canUpdate";
      editHeader.title = 'Editer';
      editHeader.width = '60px';
      listHeaders.push(editHeader);

      const adminAttribs = this.adminAttribs.filter((att) => att.tableCol > 0 && att.type != 'htmlAttrib').sort((a1, a2) => a1.tableCol - a2.tableCol);
      adminAttribs.forEach((attrib) => {
        attrib.sortable = true;
        listHeaders.push(attrib);
      })
      const deleteHeader = new AdminAttrib();
      deleteHeader.key = "canDelete";
      deleteHeader.title = 'Supprimer';
      deleteHeader.width = '60px';
      listHeaders.push(deleteHeader);
      return listHeaders;
    },
    addLabel(): string {
      return 'Ajouter';
    },
    items(): unknown[] {
      if (this.adminInfo.table != this.lastTable || this.route.sectionObjId != this.lastRoute.sectionObjId) {
        this.changeTable();
      }
      return this.listData.reccords;
    },
    adminConfig(): AdminConfig {
      return this.listData.adminConfig;
    },
    table(): string {
      return this.adminInfo.table;
    },
    tableName(): string {
      if (this.adminInfo.label == "" && this.adminInfos.length > 0) {
        const info = this.adminInfos.find(a => a.table == this.route.section);
        if (info) {
          return info.label;
        }
      }
      return this.adminInfo.label;
    },
    adminAttribs(): AdminAttrib[] {
      return getAdminAttribs(this.table, this.adminConfig.fields);
    },
    adminExport(): AdminExport {
      const res = new AdminExport();
      res.service = 'Admin';
      res.table = this.table;
      res.dataIds = this.displayedIds;
      res.possibleType = this.adminConfig.exportTypes;
      res.exportSelected = this.adminConfig.exportSelected;
      return res;
    },
    canExport() {
      return this.adminConfig.exportTypes.length > 0;
    },
    disableExport() {
      return this.displayedIds.length != 1 && this.adminConfig.exportSelected;
    },
    adminImport(): AdminImport {
      const res = new AdminImport();
      res.service = 'Admin';
      res.table = this.table;
      return res;
    },
    listConfig(): ColumnConfig[] {
      return this.config.columnsConfig;
    }
  },
  watch: {
    "toggleNotif": [{
      handler: "onToggleNotifChanged"
    }],
    "items": [{
      handler: "onItemsChanged"
    }]
  },
  created(): void {
    this.loadTypes();
  },

  methods: {
    changeTable() {
      this.lastRoute = Route.fromJson(this.route);
      this.loading = true;
      this.resetActiveFilters();
      this.loadTable();
      this.pageNumber = 1;
      this.lastTable = this.adminInfo.table;
    },
    loadTable(): void {
      const filter = new ListFilter();
      filter.tableName = this.adminInfo.table;
      filter.page = this.pageNumber;
      filter.displayedHeaders = this.displayedHeaders;
      filter.searchText = this.searchText;
      filter.columnsFilter = this.activeFilters;
      if (this.route.sectionObjId > 0) {
        filter.selectedId = this.route.sectionObjId;
      }
      Admin.loadTable(filter).then((res) => {
        this.listData = new ListData();
        this.listData = res;
        this.pageNumber = res.currentPage;
        if (this.adminInfo.table == "HtmlAttribs") {
          const adminAttrib = new AdminAttrib();
          adminAttrib.type = 'html'
          this.listData.adminConfig.fields.push(adminAttrib);
        }
        this.loading = false;
        document.body.style.cursor = 'default';
      });
    },
    loadTypes(): Promise<boolean> {
      return new Promise((resolve) => {
        Admin.listTableInfo().then((infos: AdminInfo[]) => {
          this.adminInfos = infos;
          resolve(true);
        });
      });
    },
    reloadTable(): void {
      if (this.route.sectionObjId) {
        const route = Route.fromJson(this.route);
        route.sectionObjId = 0;
        this.$emit('selectItem', route);
      } else {
        this.loadTable();
      }
    },
    setTableFilters(columns: AdminAttrib[]): void {
      this.tableFilters = {};
      for (let i = 0; i < columns.length; i++) {
        if (columns[i].isFilterable
            ||
            (columns[i].type == 'bool' && columns[i].value != 'canUpdate' && columns[i].value != 'canDelete')
            ||
            (columns[i].choix != null && columns[i].choix.length > 0)) {
          this.tableFilters[columns[i].value] = [];
        }
      }
    },
    editItem(item: unknown) {
      const extend = editItem(this.table, this.adminConfig, item);
      if (extend) {
        this.section = extend;
      } else {
        this.section = new Section();
        this.section.component = SectionAdmin;
        this.section.type = 'data';
        this.section.data = item;
        this.section.propType = 'config';
        this.section.propData = this.adminAttribs;
        this.section.adminInfo = this.adminInfo;
      }
      if (this.section.component) {
        this.showSection = true;
      }
    },
    cloneTableItem(item: unknown) {
      const extend = cloneItem(this.table, item);
      if (extend) {
        this.section = extend;
        this.showSection = true;
      }
    },
    deleteItem(obj: any) {
      const index = this.items.findIndex((x: any) => x.id == obj.id);
      if (index >= 0) {
        const confirm = RessourceFwkButton(FwkButtons.CONFIRMER) as Ressource;
        const dialog = new Dialog();
        dialog.buttons = [];
        dialog.buttons.push(RessourceFwkButton(FwkButtons.CONFIRMER).label);
        dialog.label = confirm.label;
        openAlert(dialog).then((res: string) => {
          if (res) {
            const adminObj = new AdminObj();
            adminObj.table = this.table;
            adminObj.data = obj;
            adminObj.action = AdminObjActions.DELETE;
            Admin.updateTable(adminObj).then(() => {
              this.items.splice(index, 1);
              if (this.pageNumber == this.pageCount && this.items.length == 0) {
                this.pageNumber = this.pageNumber - 1;
              }
              this.loadTable();
            });
          }
        });
      }
    },
    addItem() {
      const extend = addItem(this.table, this.adminConfig, this.items);
      if (extend) {
        this.section = extend;
        this.showSection = true;
      } else {
        const item = new Object();
        this.editItem(item);
      }
    },
    updateTable(obj: any) {
      if (!obj) {
        this.loadTable();
      }
      const adminObj = new AdminObj();
      adminObj.table = this.table;
      adminObj.data = obj;
      const index = this.items.findIndex((x: any) => x.id == obj.id);
      if (index >= 0) {
        if (!this.section.showButton) {
          adminObj.action = AdminObjActions.UPDATE;
          Admin.updateTable(adminObj).then((res) => {
            this.items.splice(index, 1, res);
            this.loadTable();
          });
        } else if (obj.id) {
          this.items.splice(index, 1, obj)
        }
      } else {
        if (!this.section.showButton) {
          adminObj.action = AdminObjActions.CREATE;
          Admin.updateTable(adminObj).then((res) => {
            this.items.push(res);
            this.loadTable();
          });
        } else if (obj.id) {
          this.items.push(obj);
        }
      }
    },
    setCurrentItems(obj: any[]): void {
      this.displayedIds = [];
      obj.forEach(x => this.displayedIds.push(x.id));
    },
    openImport() {
      this.section = new Section();
      this.section.component = ImportDialog;
      this.section.type = 'config';
      this.section.data = this.adminImport;
      this.showSection = true;
    },
    openExport() {
      this.section = new Section();
      this.section.component = ExportDialog;
      this.section.type = 'config';
      this.section.data = this.adminExport;
      this.section.propType = 'fields';
      this.section.propData = this.adminAttribs;
      this.showSection = true;
    },
    closeDialog() {
      this.$emit('closeNotif');
      this.showSection = false;
    },
    notifType(item: any): string {
      let res = '';
      if (item && item.notifDefType) {
        if (item.notifDefType == NotifDefTypes.ACTION) {
          res = 'action-bg';
        } else if (item.notifDefType == NotifDefTypes.INFO) {
          res = 'info-bg';
        }
      }
      return res;
    },
    notifRowProps({item}: { item: any }): Record<string, string> {
      return {class: this.notifType(item)};
    },
    back(): void {
      this.$emit("back")
    },
    onToggleNotifChanged() {
      this.notifLoaded = false;
    },
    onItemsChanged() {
      if (!this.notifLoaded) {
        const item = this.items.find((i: any) => i.id == this.route.sectionObjId);
        if (item && !this.loading) {
          this.editItem(item);
          this.notifLoaded = true;
        }
      }
    }
  }
})

</script>
